import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import AppShell from "../components/AppShell";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useSettings } from "../context/SettingsContext";
import VisionPulseCard from "../components/VisionPulseCard";
import { SetupChecklist } from "../components/SetupChecklist";
import {
  Sparkles, BookOpenText, Users, UserSquare, CalendarDays,
  Flame, ArrowUpRight, ClipboardList, Mic
} from "lucide-react";

const motivations = [
  "Your future self is built from today's small captures.",
  "Show up. The vision compounds.",
  "Every musing is a brick in the legacy.",
  "Consistency is the loudest signal you can send.",
  "What you reflect on, you amplify.",
];

export default function Dashboard() {
  const { user } = useAuth();
  const { label } = useSettings();
  const [data, setData] = useState(null);
  const motivation = motivations[new Date().getDate() % motivations.length];

  useEffect(() => { (async () => {
    try { const { data } = await api.get("/dashboard/summary"); setData(data); } catch {}
  })(); }, []);

  const todayMusings = (data?.recent_musings || []).filter(m => (m.created_at||"").slice(0,10) === new Date().toISOString().slice(0,10)).length;

  return (
    <AppShell eyebrow={`Hello, ${user?.name?.split(" ")[0] || "Leader"}`} title="Today's Studio">
      <SetupChecklist />
      {/* Two hero CTAs — capture & generate */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-zinc-200 border border-zinc-200 mb-8">
        <Link to="/musings/new" data-testid="hero-capture"
          className="bg-white p-6 sm:p-8 lg:p-10 group hover:-translate-y-1 brand-gradient-soft">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 bg-[#0033FF] flex items-center justify-center">
              <BookOpenText className="w-6 h-6 text-white" />
            </div>
            <div className="text-[10px] tracking-[0.25em] uppercase text-[#0033FF] font-bold">Capture · 60 seconds</div>
          </div>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-black tracking-tighter leading-tight">
            Drop a musing.<br/>
            <span className="text-zinc-500">Type, speak, snap.</span>
          </h2>
          <div className="mt-6 flex items-center gap-3 text-sm text-zinc-700">
            <Mic className="w-4 h-4" />
            <span>Voice → instant transcript</span>
          </div>
          <div className="mt-2 text-xs text-zinc-500">
            <span className="font-bold text-zinc-900">{data?.musings_count ?? 0}</span> captured · <span className="font-bold text-[#0033FF]">{todayMusings}</span> today
          </div>
          <div className="mt-6 inline-flex items-center gap-2 text-xs font-bold tracking-[0.2em] uppercase text-[#0033FF] group-hover:gap-3">
            Capture now <ArrowUpRight className="w-3.5 h-3.5" />
          </div>
        </Link>

        <Link to="/content" data-testid="hero-generate"
          className="bg-white p-6 sm:p-8 lg:p-10 group hover:-translate-y-1">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 bg-zinc-900 flex items-center justify-center">
              <Sparkles className="w-6 h-6 text-[#FFB800]" />
            </div>
            <div className="text-[10px] tracking-[0.25em] uppercase text-zinc-900 font-bold">AI Studio</div>
          </div>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-black tracking-tighter leading-tight">
            Generate content.<br/>
            <span className="text-zinc-500">In your voice.</span>
          </h2>
          <div className="mt-6 text-sm text-zinc-700">
            Claude turns this week's musings into posts, emails, decks — drafted, editable, ready to ship.
          </div>
          <div className="mt-2 text-xs text-zinc-500">
            <span className="font-bold text-zinc-900">{data?.content_count ?? 0}</span> pieces drafted
          </div>
          <div className="mt-6 inline-flex items-center gap-2 text-xs font-bold tracking-[0.2em] uppercase text-zinc-900 group-hover:gap-3">
            Open studio <ArrowUpRight className="w-3.5 h-3.5" />
          </div>
        </Link>
      </div>

      {/* Motivating one-liner */}
      <div className="mb-8 border-l-4 border-[#0033FF] pl-4 py-2" data-testid="motivation-card">
        <div className="text-[10px] tracking-[0.25em] uppercase text-[#0033FF] font-bold">On the wall today</div>
        <div className="text-lg sm:text-xl font-bold tracking-tight mt-1">{motivation}</div>
      </div>

      {/* Vision Pulse */}
      <div className="mb-8">
        <VisionPulseCard />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-px bg-zinc-200 border border-zinc-200">
        {/* Today's reflections */}
        <Link to="/prompts" data-testid="card-reflections" className="bg-white p-6 hover:bg-zinc-50">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <ClipboardList className="w-5 h-5 text-[#0033FF]" />
              <div className="text-[10px] tracking-[0.25em] uppercase text-[#0033FF] font-bold">{label("prompts")}</div>
            </div>
            <ArrowUpRight className="w-4 h-4 text-zinc-400" />
          </div>
          <div className="flex items-end gap-3">
            <div className="text-5xl font-black tracking-tighter">{todayMusings}</div>
            <div className="text-sm text-zinc-600 mb-2">reflections today · keep the streak alive 🔥</div>
          </div>
          <div className="mt-3 text-xs text-zinc-500 flex items-center gap-1">
            <Flame className="w-3.5 h-3.5 text-[#FF3B30]" /> {data?.active_days_14 ?? 0} / 14 active days
          </div>
        </Link>

        {/* Upcoming drops */}
        <Link to="/schedule" data-testid="card-upcoming" className="bg-white p-6 hover:bg-zinc-50">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <CalendarDays className="w-5 h-5 text-[#0033FF]" />
              <div className="text-[10px] tracking-[0.25em] uppercase text-[#0033FF] font-bold">{label("schedule")}</div>
            </div>
            <ArrowUpRight className="w-4 h-4 text-zinc-400" />
          </div>
          {(data?.upcoming_drops || []).length === 0 ? (
            <div>
              <div className="text-2xl font-black tracking-tight">Open road ahead</div>
              <div className="text-sm text-zinc-600 mt-1">Nothing scheduled. Generate + schedule your next drop.</div>
            </div>
          ) : (
            <div>
              <div className="text-2xl font-black tracking-tight">{data.upcoming_drops.length} drop{data.upcoming_drops.length===1?"":"s"} queued</div>
              <div className="mt-3 space-y-2">
                {data.upcoming_drops.slice(0,3).map((d) => (
                  <div key={d.id} className="flex items-center justify-between text-sm">
                    <span className="text-[10px] tracking-[0.2em] uppercase text-zinc-500 font-bold w-20 shrink-0">{d.channel}</span>
                    <span className="font-mono text-xs text-zinc-700">{new Date(d.scheduled_at).toLocaleString()}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </Link>

        {/* Team / People */}
        <Link to="/team" data-testid="card-team" className="bg-white p-6 hover:bg-zinc-50">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Users className="w-5 h-5 text-[#0033FF]" />
              <div className="text-[10px] tracking-[0.25em] uppercase text-[#0033FF] font-bold">{label("team")}</div>
            </div>
            <ArrowUpRight className="w-4 h-4 text-zinc-400" />
          </div>
          <div className="flex items-end gap-3">
            <div className="text-5xl font-black tracking-tighter">{data?.team_count ?? 0}</div>
            <div className="text-sm text-zinc-600 mb-2">{data?.team_count === 1 ? "person" : "people"} you're stewarding</div>
          </div>
          <div className="mt-3 text-xs text-zinc-500">Life goals, hobbies, birthdays — at your fingertips.</div>
        </Link>

        {/* Customers / Members */}
        <Link to="/customers" data-testid="card-customers" className="bg-white p-6 hover:bg-zinc-50">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <UserSquare className="w-5 h-5 text-[#0033FF]" />
              <div className="text-[10px] tracking-[0.25em] uppercase text-[#0033FF] font-bold">{label("customers")}</div>
            </div>
            <ArrowUpRight className="w-4 h-4 text-zinc-400" />
          </div>
          <div className="flex items-end gap-3">
            <div className="text-5xl font-black tracking-tighter">{data?.customers_count ?? 0}</div>
            <div className="text-sm text-zinc-600 mb-2">stories worth remembering</div>
          </div>
          <div className="mt-3 text-xs text-zinc-500">Each success story is proof your vision is working.</div>
        </Link>
      </div>

      {/* Recent musings preview */}
      <div className="mt-8 border border-zinc-200 bg-white">
        <div className="flex items-center justify-between p-5 border-b border-zinc-200">
          <div className="text-[10px] tracking-[0.25em] uppercase text-[#0033FF] font-bold">Latest captures</div>
          <Link to="/musings" data-testid="see-all-musings" className="text-xs font-bold tracking-wider uppercase text-zinc-900 hover:text-[#0033FF]">See all →</Link>
        </div>
        <div className="divide-y divide-zinc-200">
          {(data?.recent_musings || []).length === 0 && (
            <div className="p-6 text-sm text-zinc-500" data-testid="no-recent-musings">No musings yet. Start with a single thought above.</div>
          )}
          {(data?.recent_musings || []).slice(0,5).map((m) => (
            <div key={m.id} className="p-4" data-testid={`recent-musing-${m.id}`}>
              <div className="flex items-center gap-2 mb-1">
                <span className="text-[10px] tracking-[0.2em] uppercase font-bold text-[#0033FF]">{m.musing_type}</span>
                <span className="text-sm font-semibold truncate">{m.title || "(untitled)"}</span>
                <span className="text-xs text-zinc-500 font-mono ml-auto">{new Date(m.created_at).toLocaleDateString()}</span>
              </div>
              <div className="text-sm text-zinc-700 line-clamp-2">{m.content}</div>
            </div>
          ))}
        </div>
      </div>
    </AppShell>
  );
}
